import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { clearAccessToken, setAccessToken } from '../../utils/authToken';
import { extractUserIdFromAccessToken } from '../../utils/jwt';

export type LoginSuccessPayload = {
  username: string;
  accessToken: string;
  tokenType?: string;
  userId?: string | null;
  email?: string | null;
  fullName?: string | null;
};

export type ProfileDetailsPayload = {
  email?: string | null;
  fullName?: string | null;
  username?: string | null;
};

type AuthState = {
  isAuthenticated: boolean;
  username: string | null;
  userId: string | null;
  email: string | null;
  fullName: string | null;
  accessToken: string | null;
  tokenType: string | null;
};

const initialState: AuthState = {
  isAuthenticated: false,
  username: null,
  userId: null,
  email: null,
  fullName: null,
  accessToken: null,
  tokenType: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
      const userId =
        action.payload.userId?.trim() ||
        extractUserIdFromAccessToken(action.payload.accessToken) ||
        null;
      state.isAuthenticated = true;
      state.username = action.payload.username;
      state.userId = userId;
      state.email = action.payload.email?.trim() || null;
      state.fullName = action.payload.fullName?.trim() || null;
      state.accessToken = action.payload.accessToken;
      state.tokenType = action.payload.tokenType ?? 'bearer';
      setAccessToken(action.payload.accessToken);
    },
    updateProfileDetails: (state, action: PayloadAction<ProfileDetailsPayload>) => {
      if (action.payload.username !== undefined) {
        state.username = action.payload.username?.trim() || state.username;
      }
      if (action.payload.email !== undefined) {
        state.email = action.payload.email?.trim() || null;
      }
      if (action.payload.fullName !== undefined) {
        state.fullName = action.payload.fullName?.trim() || null;
      }
    },
    logout: () => {
      clearAccessToken();
      return {
        isAuthenticated: false,
        username: null,
        userId: null,
        email: null,
        fullName: null,
        accessToken: null,
        tokenType: null,
      };
    },
  },
});

export const { loginSuccess, updateProfileDetails, logout } = authSlice.actions;

export default authSlice.reducer;
